import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  FiRefreshCw, FiSearch, FiRadio, FiArrowUpRight, FiArrowDownLeft,
  FiX, FiChevronDown, FiChevronRight, FiAlertTriangle
} from 'react-icons/fi';
import { getSignals, cancelSignal, getSignalCommands, signalStatusMeta } from '../../services/signalService';
import { getSources } from '../../services/signalSourceService';
import Button from '../../components/common/Button';
import { commandTypeName, commandStatusMeta, fmtTime } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';

const SignalList = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [status, setStatus] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [expandedId, setExpandedId] = useState(null); // 展开分发详情的信号

  const { data, isLoading, error, refetch } = useQuery(
    ['signals', page, limit, status, sourceId],
    () => getSignals({ page, limit, status, sourceId }),
    { keepPreviousData: true, refetchInterval: 30000 }
  );
  const signals = data?.signals || [];

  const { data: sourcesData } = useQuery('signalSources', getSources);
  const sources = sourcesData?.sources || [];

  const cancelMutation = useMutation(cancelSignal, {
    onSuccess: () => {
      toast.success('信号已取消');
      queryClient.invalidateQueries(['signals']);
    },
    onError: (err) => toast.error(err.message || '取消失败')
  });

  // 分发详情(展开行)
  const detailQuery = useQuery(
    ['signalCommands', expandedId],
    () => getSignalCommands(expandedId, { limit: 100 }),
    { enabled: !!expandedId }
  );

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">信号记录</h1>
          <p className="text-[#656d76] text-sm mt-1">策略端推送的开仓/平仓信号及分发执行情况（跟单资格、分组、到期均在服务端实时校验）</p>
        </div>
        <Button onClick={() => refetch()} variant="secondary" icon={<FiRefreshCw />} className="px-3" />
      </div>

      {/* 筛选 */}
      <div className="bg-white rounded-md p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <FiSearch className="text-[#656d76]" />
          <select
            value={sourceId}
            onChange={(e) => { setSourceId(e.target.value); setPage(1); }}
            className="bg-[#f6f8fa] rounded-md text-[#1f2328] py-2 px-3 text-sm focus:outline-none input-primary"
          >
            <option value="">全部策略源</option>
            {sources.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="bg-[#f6f8fa] rounded-md text-[#1f2328] py-2 px-3 text-sm focus:outline-none input-primary"
          >
            <option value="">全部状态</option>
            <option value="pending">待分发</option>
            <option value="dispatched">已分发</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            <div className="skeleton h-12" /><div className="skeleton h-12" /><div className="skeleton h-12" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-red-500 mb-4">获取信号列表失败</div>
            <Button onClick={() => refetch()} variant="primary">重试</Button>
          </div>
        ) : signals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#656d76] px-6">
            <div className="w-16 h-16 rounded-md brand-soft brand-text flex items-center justify-center mb-5">
              <FiRadio className="text-3xl" />
            </div>
            <p className="text-xl text-[#24292f] font-medium">还没有信号记录</p>
            <p className="mt-2 text-center">策略端推送器 EA 接入后，这里会实时显示每一笔开仓/平仓信号的分发与执行情况</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="data-table min-w-[920px]">
              <thead>
                <tr>
                  <th className="py-3 px-4 w-8"></th>
                  <th className="py-3 px-4 text-left">时间</th>
                  <th className="py-3 px-4 text-left">策略源</th>
                  <th className="py-3 px-4 text-left">类型</th>
                  <th className="py-3 px-4 text-left">方向/品种</th>
                  <th className="py-3 px-4 text-left">手数</th>
                  <th className="py-3 px-4 text-left">signalId</th>
                  <th className="py-3 px-4 text-left">状态</th>
                  <th className="py-3 px-4 text-left">分发数</th>
                  <th className="py-3 px-4 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {signals.map(sig => {
                  const st = signalStatusMeta(sig.status);
                  const expanded = expandedId === sig.id;
                  return (
                    <React.Fragment key={sig.id}>
                      <tr className={expanded ? 'bg-[#f6f8fa]' : ''}>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => toggleExpand(sig.id)}
                            className="p-1 text-[#656d76] hover:text-[#1f2328] rounded hover:bg-[#eaeef2]"
                            title="查看分发详情"
                          >
                            {expanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-[#57606a] text-xs whitespace-nowrap">{fmtTime(sig.createdAt)}</td>
                        <td className="py-3 px-4">
                          <span className="text-xs px-2 py-1 rounded-full bg-[#f6f8fa] text-[#57606a] border border-[#d0d7de]">
                            {sig.source?.name || `#${sig.sourceId}`}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded ${sig.signalType === 'open'
                            ? 'bg-[#ddf4ff] text-[#0969da]'
                            : 'bg-[#fbeaf0] text-[#993556]'}`}>
                            {sig.signalType === 'open' ? '开仓' : '平仓'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {sig.signalType === 'open' ? (
                            <span className="flex items-center gap-1.5 text-sm">
                              {sig.direction === 'buy'
                                ? <FiArrowUpRight className="text-[#cf222e]" />
                                : <FiArrowDownLeft className="text-[#1a7f37]" />}
                              <span className="font-mono text-[#1f2328]">{sig.symbol}</span>
                              <span className="text-xs text-[#57606a]">{sig.direction === 'buy' ? '买' : '卖'}</span>
                            </span>
                          ) : (
                            <span className="font-mono text-[#1f2328] text-sm">{sig.symbol}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-[#1f2328] text-sm">{sig.volume ?? '—'}</td>
                        <td className="py-3 px-4 font-mono text-xs text-[#57606a] max-w-[10rem] truncate" title={sig.signalId}>
                          {sig.signalId}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded ${st.cls}`}>{st.name}</span>
                        </td>
                        <td className="py-3 px-4 text-[#1f2328] text-sm">
                          {sig.status === 'pending' ? '—' : sig.dispatchCount}
                          {sig.dispatchCount === 0 && sig.status === 'dispatched' && (
                            <span className="block text-[10px] text-[#9a6700] max-w-[6rem] leading-tight mt-0.5" title={sig.dispatchNote || ''}>
                              {sig.dispatchNote}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {sig.status === 'pending' && (
                            <button
                              onClick={() => cancelMutation.mutate(sig.id)}
                              disabled={cancelMutation.isLoading}
                              className="flex items-center gap-1 text-xs text-[#cf222e] border border-[#ffebe9] bg-white rounded-md px-2.5 py-1.5 hover:bg-[#fff5f5] disabled:opacity-40 ml-auto"
                            >
                              <FiX /> 取消
                            </button>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={10} className="bg-[#f6f8fa] px-4 py-3">
                            {detailQuery.isLoading ? (
                              <div className="text-xs text-[#656d76] py-2">加载分发详情...</div>
                            ) : detailQuery.error ? (
                              <div className="text-xs text-[#cf222e] py-2">加载失败</div>
                            ) : (detailQuery.data?.commands || []).length === 0 ? (
                              <div className="text-xs text-[#656d76] py-2">该信号没有生成任何指令（无符合资格的授权码）</div>
                            ) : (
                              <div className="space-y-1.5">
                                <div className="text-xs text-[#57606a] font-medium">
                                  分发指令（{(detailQuery.data?.commands || []).length} 条）
                                </div>
                                {(detailQuery.data?.commands || []).map(cmd => {
                                  const cs = commandStatusMeta(cmd.status);
                                  return (
                                    <div key={cmd.id} className="flex items-center gap-3 bg-white border border-[#eaeef2] rounded-md px-3 py-2 text-xs">
                                      <span className="font-medium text-[#1f2328] w-20 shrink-0">{commandTypeName(cmd.commandType)}</span>
                                      <span className={`px-1.5 py-0.5 rounded shrink-0 ${cs.cls}`}>{cs.name}</span>
                                      <span className="font-mono text-[#57606a] truncate flex-1">{cmd.licenseKey}</span>
                                      <span className="text-[#8b949e] shrink-0 hidden md:block">{fmtTime(cmd.createdAt)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页 */}
        {signals.length > 0 && (
          <div className="p-4 flex flex-wrap justify-between items-center gap-3 border-t border-[#d0d7de]">
            <div className="text-[#656d76] text-sm">
              总计 {data?.totalItems || 0} 条信号，共 {data?.totalPages || 1} 页
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setPage(page > 1 ? page - 1 : 1)} disabled={page <= 1} variant="secondary" size="sm">上一页</Button>
              <span className="inline-flex items-center px-3 py-1 rounded bg-[#f6f8fa] text-[#1f2328] text-sm">{page} / {data?.totalPages || 1}</span>
              <Button onClick={() => setPage(page < (data?.totalPages || 1) ? page + 1 : page)} disabled={page >= (data?.totalPages || 1)} variant="secondary" size="sm">下一页</Button>
            </div>
          </div>
        )}
      </div>

      {/* 提示 */}
      <div className="flex items-start gap-2 bg-[#fff8c5] border border-[#f0d875] rounded-md p-3 text-xs text-[#7d4e00]">
        <FiAlertTriangle className="shrink-0 mt-0.5" />
        <span>
          开仓指令仅发给"跟单开关已打开、跟单未到期、分组匹配"的授权码；关停或到期后，排队中的开仓指令会在客户 EA 下一轮拉取时（约0.5秒）自动作废。
        </span>
      </div>
    </div>
  );
};

export default SignalList;
